import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardContent, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Brain, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

const INSTITUTION_TYPES = [
  { id: "universidad", label: "Universidad" },
  { id: "hospital", label: "Hospital / centro sanitario" },
  { id: "clinica", label: "Clínica privada" },
  { id: "entidad_social", label: "Entidad / asociación social" },
  { id: "investigacion", label: "Centro de investigación" },
  { id: "particular", label: "Uso particular" },
  { id: "otro", label: "Otro" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: "patient",
    institution: "", institution_type: "universidad",
    purpose: "", country: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (!res.ok) { toast.error(res.error); return; }
    setSent(res.data);
  };

  if (sent) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6" data-testid="register-pending-screen">
        <Card className="max-w-lg w-full rounded-2xl border-border">
          <CardHeader className="text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/15 text-primary grid place-items-center mb-2">
              <ShieldCheck size={26} />
            </div>
            <CardTitle className="heading text-2xl">Solicitud enviada</CardTitle>
            <CardDescription>Tu cuenta está pendiente de autorización.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-center">
            <p className="text-foreground/85">
              {sent.message || "Un administrador revisará tu solicitud. Recibirás confirmación cuando se apruebe."}
            </p>
            <div className="text-sm text-muted-foreground border border-border rounded-xl p-4 bg-secondary/60 text-left">
              <p><b>Nombre:</b> {sent.user.name}</p>
              <p><b>Correo:</b> {sent.user.email}</p>
              <p><b>Rol solicitado:</b> {sent.user.role}</p>
              <p><b>Entidad:</b> {sent.user.institution} ({sent.user.institution_type})</p>
            </div>
          </CardContent>
          <CardFooter>
            <Button className="w-full rounded-full h-11" onClick={() => nav("/login")} data-testid="pending-goto-login">
              Ir a inicio de sesión
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-secondary">
        <div className="bg-background rounded-2xl px-4 py-3 inline-flex self-start border border-border">
          <Logo size="md" testid="register-brand-logo" />
        </div>
        <div>
          <h1 className="heading text-4xl font-extrabold leading-tight text-foreground">
            Solicita acceso a la <br />plataforma clínica.
          </h1>
          <p className="mt-4 text-foreground/70 text-lg max-w-md">
            Cada solicitud es revisada y autorizada por un administrador para garantizar el uso profesional del contenido.
          </p>
          <ul className="mt-6 space-y-2 text-foreground/75">
            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-primary" /> Datos protegidos y auditados</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-primary" /> Contenido 100% original</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-primary" /> Uso clínico, docente e investigador</li>
          </ul>
        </div>
        <div className="text-sm opacity-70">© 2026 SocialCog Training®</div>
      </div>

      <div className="flex items-center justify-center px-6 py-12 bg-background">
        <Card className="w-full max-w-lg border-border rounded-2xl">
          <CardHeader>
            <CardTitle className="heading text-2xl">Solicitar cuenta</CardTitle>
            <CardDescription>
              ¿Ya tienes cuenta aprobada? <Link to="/login" className="underline text-primary" data-testid="link-login">Inicia sesión</Link>
            </CardDescription>
          </CardHeader>
          <form onSubmit={submit}>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Nombre completo</Label>
                  <Input required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 h-11" data-testid="reg-name-input" />
                </div>
                <div>
                  <Label>País</Label>
                  <Input value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    placeholder="España, México..."
                    className="mt-2 h-11" data-testid="reg-country-input" />
                </div>
              </div>
              <div>
                <Label>Correo profesional</Label>
                <Input type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 h-11" data-testid="reg-email-input" />
              </div>
              <div>
                <Label>Contraseña</Label>
                <Input type="password" required minLength={6} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-2 h-11" data-testid="reg-password-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Rol solicitado</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-2 h-11" data-testid="reg-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="patient">Paciente</SelectItem>
                      <SelectItem value="professional">Profesional clínico</SelectItem>
                      <SelectItem value="researcher">Investigador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo de entidad</Label>
                  <Select value={form.institution_type} onValueChange={(v) => setForm({ ...form, institution_type: v })}>
                    <SelectTrigger className="mt-2 h-11" data-testid="reg-inst-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INSTITUTION_TYPES.map(i => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Nombre de la entidad</Label>
                <Input required value={form.institution}
                  onChange={(e) => setForm({ ...form, institution: e.target.value })}
                  placeholder="Universidad, hospital, asociación..."
                  className="mt-2 h-11" data-testid="reg-institution-input" />
              </div>
              <div>
                <Label>Motivo de uso (opcional)</Label>
                <Textarea value={form.purpose} rows={3}
                  onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                  placeholder="Ej: docencia de máster en neuropsicología, ensayo clínico, práctica clínica..."
                  className="mt-2" data-testid="reg-purpose-input" />
              </div>
              <div className="text-xs text-muted-foreground bg-secondary/60 border border-border rounded-lg px-3 py-2">
                <ShieldCheck size={12} className="inline mr-1" />
                Tu solicitud quedará <b>pendiente</b> hasta que un administrador la autorice.
              </div>
            </CardContent>
            <CardFooter>
              <Button type="submit" disabled={loading} className="w-full h-12 text-base rounded-full" data-testid="reg-submit-btn">
                <UserPlus size={18} /> {loading ? "Enviando..." : "Enviar solicitud"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
